import { of, merge, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  debounceTime,
} from 'rxjs/operators';
import moment from 'moment';
import {
  ImplementedAction,
  ImplementedActionTypes,
  ImplementedState,
} from '../../types/implemented';
import { Organization } from '../../types/api';
import {
  Resource,
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import { actions } from '../index';
import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';
import { getRequiredSessionToken } from '../../utils/session-token';

import createRxStore, { rxDispatch, ofType } from '..';
import SessionStore from '../session';
import { SessionStatus } from '../../types/session';
import { SORT_EMPTY } from '../../types/sort';

import {
  fetchInitialSpaces,
  fetchNextPageOfSpaces,
} from '../../rx-actions/load-implemented';

export const initialState: ImplementedState = {
  selectedSpaceIds: [],
  filters: [],
  searchText: '',
  sort: SORT_EMPTY,

  page: 1,

  organizations: [],

  resource: RESOURCE_IDLE,
};

function getYesterdaysDate(): string {
  return moment().subtract(1, 'day').format('YYYY-MM-DD');
}

function reducer(
  state: ImplementedState,
  action: ImplementedAction,
): ImplementedState {
  switch (action.type) {
    case ImplementedActionTypes.IMPLEMENTED_CHANGE_PAGE:
      return {
        ...state,
        filters: action.filters,
        sort: action.sort,
        searchText: action.searchText,

        page: 1,

        resource: RESOURCE_IDLE,
      };

    case ImplementedActionTypes.IMPLEMENTED_LOAD_COMPLETE:
      return {
        selectedSpaceIds: state.selectedSpaceIds,
        searchText: action.searchText,
        sort: action.sort,
        filters: action.filters,

        organizations: action.organizations,

        page: 1,

        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            total: action.total,
            spaces: action.spaces,
            fetchingMoreSpaces: RESOURCE_IDLE,
          },
        },
      };

    case ImplementedActionTypes.IMPLEMENTED_LOAD_START:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };

    case ImplementedActionTypes.IMPLEMENTED_LOAD_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };

    case ImplementedActionTypes.IMPLEMENTED_SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.text,
        page: 1,
      };

    case ImplementedActionTypes.IMPLEMENTED_SET_SORT:
      return {
        ...state,
        sort: {
          column: action.column,
          direction: action.direction,
        },
        page: 1,
        resource: RESOURCE_LOADING,
      };

    case ImplementedActionTypes.IMPLEMENTED_SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
        selectedSpaceIds: [],
      };

    case ImplementedActionTypes.IMPLEMENTED_CHANGE_CHECKS:
      return {
        ...state,
        selectedSpaceIds: action.checked
          ? Array.from(new Set([...state.selectedSpaceIds, ...action.ids]))
          : state.selectedSpaceIds.filter((id) => !action.ids.includes(id)),
      };

    case ImplementedActionTypes.IMPLEMENTED_SET_PAGE:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        page: action.value,

        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSpaces: RESOURCE_LOADING,
          },
        },
      };

    case ImplementedActionTypes.IMPLEMENTED_NEXT_PAGE_LOAD_ERROR:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSpaces: {
              status: ResourceStatus.ERROR,
              error: action.error,
            },
          },
        },
      };

    case ImplementedActionTypes.IMPLEMENTED_NEXT_PAGE_LOAD_COMPLETE:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            spaces: [...state.resource.data.spaces, ...action.spaces],
            fetchingMoreSpaces: RESOURCE_IDLE,
          },
        },
      };

    default:
      return state;
  }
}

const ImplementedStore = createRxStore(
  'ImplementedStore',
  initialState,
  reducer,
);

// =================================
// SIDE EFFECT: When the filters / search / sort updates, update the query parameters
// =================================
actions
  .pipe(
    ofType(
      ImplementedActionTypes.IMPLEMENTED_SET_FILTERS,
      ImplementedActionTypes.IMPLEMENTED_SET_SEARCH_TEXT,
      ImplementedActionTypes.IMPLEMENTED_SET_SORT,
    ),
    debounceTime(500),
    switchMap(() => ImplementedStore.pipe(take(1))),
  )
  .subscribe((state) => {
    const params = serializeListViewState(
      state.filters,
      state.searchText,
      state.sort,
    );
    updateQueryString(params);
  });

// =================================
// SIDE EFFECT: Load first page of space data when one of these occurs:
// 1. Page is initially loaded
// 3. Search text is updated (has a debounce in it)
// 3. Filters or sort are updated
// =================================
const initialLoadStream = actions.pipe(
  ofType<{ organizationId: Organization['id'] | null }>(
    ImplementedActionTypes.IMPLEMENTED_CHANGE_PAGE,
  ),
  switchMap(
    (action) =>
      combineLatest(ImplementedStore.pipe(take(1)), SessionStore.pipe(take(1))),
    (action, [implementedState, sessionState]) => ({
      implementedState,
      sessionState,
      organizationId: action.organizationId,
    }),
  ),
);

const searchTextUpdateStream = actions.pipe(
  ofType(ImplementedActionTypes.IMPLEMENTED_SET_SEARCH_TEXT),
  debounceTime(1000),
  switchMap(
    () =>
      combineLatest(ImplementedStore.pipe(take(1)), SessionStore.pipe(take(1))),
    (action, [implementedState, sessionState]) => ({
      implementedState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

const sortFilterUpdateStream = actions.pipe(
  ofType(
    ImplementedActionTypes.IMPLEMENTED_SET_FILTERS,
    ImplementedActionTypes.IMPLEMENTED_SET_SORT,
  ),
  switchMap(
    () =>
      combineLatest(ImplementedStore.pipe(take(1)), SessionStore.pipe(take(1))),
    (action, [implementedState, sessionState]) => ({
      implementedState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

merge(
  initialLoadStream,
  searchTextUpdateStream,
  sortFilterUpdateStream,
).subscribe(({ implementedState, sessionState, organizationId }) => {
  if (sessionState.status !== SessionStatus.LOGGED_IN) {
    return;
  }

  fetchInitialSpaces(
    rxDispatch,
    sessionState.token,
    implementedState.filters,
    implementedState.sort,
    implementedState.searchText,
    organizationId,
  );
});

// =================================
// SIDE EFFECT: when a user reaches the end of the page, load more data
// =================================
actions
  .pipe(
    ofType(ImplementedActionTypes.IMPLEMENTED_SET_PAGE),
    switchMap(
      () =>
        combineLatest(
          ImplementedStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [implementedState, sessionState]) => ({
        action,
        implementedState,
        sessionState,
      }),
    ),
  )
  .subscribe(({ action, implementedState, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }

    const a = action as any;

    fetchNextPageOfSpaces(
      rxDispatch,
      sessionState.token,
      implementedState.filters,
      implementedState.sort,
      implementedState.searchText,
      a.value,
      sessionState.selectedOrganizationId,
    );
  });

export default ImplementedStore;
